<template>
  <div ref="scroller" class="pdf-scroller" @scroll="onScroll">
    <div
      v-for="p in pages"
      :key="p.n"
      ref="pageEls"
      class="manual-page"
      :data-page="p.n"
      :style="{ height: p.height + 'px' }"
    ></div>
  </div>
</template>

<script>
import { createPdfViewer } from "@/pdfViewer";

export default {
  name: "pdf-viewer",
  props: {
    url: { type: String, default: null },
  },
  data() {
    return {
      pages: [],
      pageSizes: [],
    };
  },
  watch: {
    url: {
      handler(value) {
        this.load(value);
      },
      immediate: false,
    },
  },
  mounted() {
    // Task 1 hands us two deliberately separate channels: onError is called
    // ONLY for a single page's render failure (pdfViewer.js report()), while a
    // document-level failure REJECTS open() and lands in load()'s catch below.
    // Forwarding onError into $emit("error") collapses them, and the modal
    // turns that into loadError, replacing the whole document with the error
    // panel because one page failed — which the spec forbids by name.
    this.viewer = createPdfViewer({
      // An internal link asks to jump to a page; this component owns scrolling.
      onNavigate: (pageNumber) => this.goToPage(pageNumber),
      onError: (err) => {
        console.error("PdfViewer: page render failed, other pages unaffected:", err);
        this.$emit("page-error", err);
      },
    });
    this.observer = new IntersectionObserver(this.onIntersect, {
      root: this.$refs.scroller,
      // One root-height of lookahead either side: the visible page plus its
      // neighbours are rendered, everything else is released.
      rootMargin: "100% 0px",
      threshold: 0,
    });
    this.onResize = () => this.computeHeights();
    window.addEventListener("resize", this.onResize);
    // Bootstrap defers _showElement (which sets display:block) behind the
    // backdrop fade for a `modal fade` with a static backdrop, so this
    // scroller can still be display:none — clientWidth 0 — when open()
    // resolves. A window resize never fires for that, so watch the element.
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.computeHeights());
      this.resizeObserver.observe(this.$refs.scroller);
    }
    if (this.url) this.load(this.url);
  },
  beforeDestroy() {
    this._torn = true;
    window.removeEventListener("resize", this.onResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.observer) this.observer.disconnect();
    if (this.viewer) this.viewer.destroy();
  },
  methods: {
    async load(url) {
      if (this.observer) this.observer.disconnect();
      this.pages = [];
      this.pageSizes = [];
      if (!url) return;

      try {
        const result = await this.viewer.open(url);
        if (!result || this._torn) return; // superseded, or component gone
        this.pageSizes = result.pageSizes;
        this.computeHeights();
        this.$emit("loaded", result.numPages);
        await this.$nextTick();
        if (this._torn) return;
        // Re-measure now that the pages exist: the first pass ran before the
        // vertical scrollbar appeared, so placeholders were 2-3% wider than
        // the canvases mountPage will size from el.clientWidth.
        //
        // No observePages()/onScroll() here: computeHeights attaches the
        // observer itself the first time it populates pages, and this.pages was
        // cleared at the top of load(), so that first-population branch always
        // runs. Calling them again here just did the work twice.
        this.computeHeights();
      } catch (err) {
        this.$emit("error", err);
      }
    },
    async computeHeights() {
      const sc = this.$refs.scroller;
      if (!sc || !this.pageSizes.length) return;
      // No `|| 1` fallback: a hidden container would bake 1px placeholders
      // permanently. Bail and let the ResizeObserver call us back with a real
      // width instead.
      const width = sc.clientWidth;
      if (!width) return;
      const hadPages = this.pages.length > 0;
      this.pages = this.pageSizes.map((size, idx) => ({
        n: idx + 1,
        height: Math.round(width * (size.h / size.w)),
      }));
      if (hadPages) {
        // Widths changed for pages that are already mounted. Their canvases
        // rescale themselves, but annotation geometry is baked in CSS pixels at
        // mount time, so it has to be re-applied or every link is offset.
        await this.$nextTick();
        if (this._torn) return;
        this.viewer.refreshAnnotations();
        return;
      }
      if (!hadPages) {
        // The placeholders have only just come into existence, because the
        // container was hidden when the document finished loading (Bootstrap
        // defers display:block behind its backdrop fade, which is what happens
        // on reopen). load() already ran observePages() against an empty
        // $refs, so the observer is watching nothing — attach it now that the
        // elements exist, or no page ever mounts and the viewer stays blank
        // with a correct header above it.
        await this.$nextTick();
        // Same teardown discipline load() uses. Without it, a close landing in
        // this window resumes after $nextTick and calls observe() on an
        // observer beforeDestroy already disconnected, re-arming a disposed
        // observer that then pins detached nodes and this destroyed instance
        // for the rest of the page session.
        if (this._torn) return;
        this.observePages();
        this.onScroll();
      }
    },
    observePages() {
      const els = this.$refs.pageEls || [];
      els.forEach((el) => this.observer.observe(el));
    },
    onIntersect(entries) {
      entries.forEach((entry) => {
        const n = Number(entry.target.dataset.page);
        if (!n) return;
        if (entry.isIntersecting) this.viewer.mountPage(n, entry.target);
        else this.viewer.unmountPage(n);
      });
    },
    onScroll() {
      const sc = this.$refs.scroller;
      if (!sc || !this.pages.length) return;
      const els = sc.getElementsByClassName("manual-page");
      const scTop = sc.getBoundingClientRect().top;
      let current = 1;
      // The current page is the last one whose top edge has scrolled to/above
      // the viewport top (small threshold so the page filling the view wins).
      for (let idx = 0; idx < els.length; idx++) {
        if (els[idx].getBoundingClientRect().top - scTop <= 80) current = idx + 1;
        else break;
      }
      this.$emit("page-change", current);
    },
    goToPage(n) {
      const sc = this.$refs.scroller;
      if (!sc || !this.pages.length) return;
      const target = Math.min(Math.max(n, 1), this.pages.length);
      const els = sc.getElementsByClassName("manual-page");
      const el = els[target - 1];
      if (!el) return;
      // Align the target page's top with the scroller's top.
      sc.scrollTop += el.getBoundingClientRect().top - sc.getBoundingClientRect().top;
    },
  },
};
</script>

<style scoped>
.pdf-scroller {
  height: 100%;
  overflow: auto;
}
.manual-page {
  display: block;
  margin-bottom: 0.5rem;
  background: #ffffff;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  position: relative;
}
.manual-page ::v-deep .annotationLayer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}
.manual-page ::v-deep .annotationLayer section {
  position: absolute;
}
.manual-page ::v-deep .annotationLayer .linkAnnotation > a {
  position: absolute;
  width: 100%;
  height: 100%;
  display: block;
}
/* vue-pdf shipped this hover affordance in its own annotationLayer.css; without
   it a table-of-contents row gives no feedback beyond the pointer cursor. */
.manual-page ::v-deep .annotationLayer .linkAnnotation > a:hover {
  opacity: 0.2;
  background: #ff0;
  box-shadow: 0 2px 10px #ff0;
}
</style>
